import {
  type Column,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Row,
  type RowData,
  type Table,
  useReactTable,
} from "@tanstack/react-table";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useImmer } from "use-immer";
import {
  type CSSProperties,
  Fragment,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import classNames from "classnames";
import { LinkButton, LinkType } from "~/common/components/Link";
import { getCssSize, getSize, getWidth } from "~/common/components/Table/TanStackTableUtils";
import { type TableOptions } from "@tanstack/table-core";
import { PopperTooltip } from "~/common/components/Tooltip";

import { isNonNull } from "~/common/utils/reducers";
import { TableScrollController } from "~/common/components/Table/TableScrollController";
import { useIsStuck } from "~/common/hooks/useIsStuck";
import {
  makeInitialWindowOffset,
  makeObserveWindowOffset,
  makeWindowScrollOffset,
} from "~/common/utils/TanstackVirtualUtils";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { styled, type Theme } from "~/common/mui";

// Only solid for now
type PinnedVariant = "solid"; // | "shadow";

const VARIANTS = {
  lines: "lines",
  stripes: "stripes",
  clear: "clear",
  product: "product",
  hsaDetails: "hsaDetails",
  productComparison: "productComparison",
  "": "",
};

const tableClasses = {
  table: "TanStackTable-table",
  tableContainer: "TanStackTable-tableContainer",
  title: "TanStackTable-title",
  header: "TanStackTable-header",
  headerCell: "TanStackTable-headerCell",
  bodyRow: "TanStackTable-bodyRow",
  evenRow: "TanStackTable-evenRow",
  oddRow: "TanStackTable-oddRow",
  bodyCell: "TanStackTable-bodyCell",
  sortHeader: "TanStackTable-sortHeader",
  sortIcon: "TanStackTable-sortIcon",
  sortIconAsc: "TanStackTable-sortIconAsc",
  sortIconDesc: "TanStackTable-sortIconDesc",
  pinnedLeft: "TanStackTable-pinnedLeft",
  pinnedLeftLast: "TanStackTable-pinnedLeftLast",
  pinnedRight: "TanStackTable-pinnedRight",
  pinnedRightFirst: "TanStackTable-pinnedRightFirst",
  cellData: "TanStackTable-cellData",
  cellAction: "TanStackTable-cellAction",
  fixedLayout: "TanStackTable-fixedLayout",
  fullWidth: "TanStackTable-fullWidth",
  sticky: "TanStackTable-sticky",
  stickyCornerSentinel: "TanStackTable-stickyCornerSentinel",
  hideScrollbar: "TanStackTable-hideScrollbar",
  isScrolledLeft: "is-scrolled-left",
  isScrolledRight: "is-scrolled-right",
  isStuck: "is-stuck",
  pinnedVariantSolid: "pinned-variant-solid",
};

export const TAN_STACK_TABLE_TITLE_CLASS_NAME = tableClasses.title;

const cellPaddingStyles = (theme: Theme) => ({
  "--v-padding": theme.spacing(1),
  "--h-padding": theme.spacing(1),
  padding: "var(--v-padding) var(--h-padding)",
  "&:first-child": {
    paddingLeft: "calc(2 * var(--h-padding))",
  },
  "&:last-child": {
    paddingRight: "calc(2 * var(--h-padding))",
  },
  [theme.breakpoints.up("desktop-s")]: {
    "--v-padding": theme.spacing(1),
    "--h-padding": theme.spacing(1),
  },
  [theme.breakpoints.up("desktop-m")]: {
    "--v-padding": theme.spacing(1),
    "--h-padding": theme.spacing(1),
  },
});

const TanStackTableRoot = styled("div")(({ theme }) => ({
  position: "relative",
  [`&.${tableClasses.isStuck}`]: {
    borderRadius: 0,
  },
  [`&.${tableClasses.tableContainer}`]: {
    overflowX: "auto",
    overflowY: "hidden",
  },
  [`&.${tableClasses.pinnedVariantSolid}.${tableClasses.isScrolledLeft} .${tableClasses.pinnedLeftLast}`]: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  [`&.${tableClasses.pinnedVariantSolid}.${tableClasses.isScrolledRight} .${tableClasses.pinnedRightFirst}`]: {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  [`& .scroll-slider__root + .${tableClasses.sticky}`]: {
    top: 48,
  },
  [`& .scroll-slider__root + .${tableClasses.stickyCornerSentinel}`]: {
    top: "47px !important",
  },
}));

const TableContainer = styled("div")({
  [`&.${tableClasses.tableContainer}`]: {
    overflowX: "auto",
    overflowY: "hidden",
  },
});

const StickyTableContainer = styled(TableContainer)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 1000,
  [`&.${tableClasses.hideScrollbar}::-webkit-scrollbar`]: {
    display: "none",
  },
  [`&.${tableClasses.stickyCornerSentinel}`]: {
    borderTop: `1px solid ${theme.palette.surface.main}`,
    top: "-1px !important",
  },
}));

const TableTitle = styled("div")(({ theme }) => ({
  ...cellPaddingStyles(theme),
  fontWeight: "bold",
  borderBottom: `3px solid ${theme.palette.divider}`,
}));

const StyledTable = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "separate",
  background: theme.palette.common.white,
  [`&.${tableClasses.sticky}`]: {
    position: "sticky",
    top: 0,
    zIndex: 1000,
  },
  [`&.${tableClasses.stickyCornerSentinel}`]: {
    borderTop: `1px solid ${theme.palette.surface.main}`,
    top: "-1px !important",
  },
  [`&.${tableClasses.fixedLayout}`]: {
    tableLayout: "fixed",
  },
  [`&.${tableClasses.fullWidth}`]: {
    width: "100%",
  },
  [`& .${tableClasses.header}, & .${tableClasses.header} > tr, & .${tableClasses.headerCell}, & > tbody, & > tbody > tr, & .${tableClasses.bodyCell}`]: {
    background: "inherit",
  },
  [`& .${tableClasses.header}`]: {
    fontWeight: "bold",
  },
  [`& .${tableClasses.header} > tr:last-child > th`]: {
    borderBottom: `3px solid ${theme.palette.divider}`,
  },
  [`&.${VARIANTS.lines} > tbody > .${tableClasses.bodyRow}:not(:last-child) > .${tableClasses.bodyCell}`]: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`&.${VARIANTS.stripes} > tbody > tr.${tableClasses.evenRow}, &.${VARIANTS.product} > tbody > tr.${tableClasses.evenRow}, &.${VARIANTS.productComparison} > tbody > tr.${tableClasses.evenRow}`]: {
    background: theme.palette.surface.bright,
  },
  [`&.${VARIANTS.stripes} > tbody > tr:hover, &.${VARIANTS.product} > tbody > tr:hover, &.${VARIANTS.productComparison} > tbody > tr:hover`]: {
    background: theme.palette.table.hoverBackground,
  },
  [`&.${VARIANTS.clear} .${tableClasses.bodyCell}, &.${VARIANTS.clear} .${tableClasses.headerCell}`]: {
    background: "transparent",
  },
  [`&.${VARIANTS.product} .${tableClasses.bodyCell}, &.${VARIANTS.product} .${tableClasses.headerCell}`]: {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
  },
  [`&.${VARIANTS.product} .${tableClasses.bodyCell}`]: {
    verticalAlign: "middle",
  },
  [`&.${VARIANTS.hsaDetails} .${tableClasses.bodyCell}`]: {
    verticalAlign: "middle",
  },
  [`&.${VARIANTS.hsaDetails} .${tableClasses.cellAction} > div`]: {
    width: 152,
  },
  [`&.${VARIANTS.hsaDetails} .${tableClasses.bodyCell}, &.${VARIANTS.hsaDetails} .${tableClasses.headerCell}`]: {
    padding: `${theme.spacing(1.25)} ${theme.spacing(1.5)}`,
  },
  [`&.${VARIANTS.productComparison} .${tableClasses.bodyCell}:not(:last-child)`]: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  [`&.${VARIANTS.hsaDetails} .${tableClasses.bodyCell}.${tableClasses.cellAction}, &.${VARIANTS.product} .${tableClasses.bodyCell}.${tableClasses.cellAction}, & .${tableClasses.bodyCell}.${tableClasses.cellAction}`]: {
    padding: 0,
    background: theme.palette.primary.main,
  },
  [`& .scha__tabular-view__action-cell-bg:not(.${tableClasses.headerCell})`]: {
    background: `${theme.palette.primary.main} !important`,
  },
  [`& .scha__tabular-view__hsa-action-cell-bg:not(.${tableClasses.headerCell})`]: {
    background: `${theme.palette.common.white} !important`,
  },
}));

const TableHeader = styled("thead")({
  [`&.${tableClasses.sticky}`]: {
    position: "sticky",
    top: 0,
    zIndex: 1000,
  },
});

const TableHeaderCell = styled("th")(({ theme }) => ({
  ...cellPaddingStyles(theme),
  background: "inherit",
  verticalAlign: "top",
}));

const BodyRow = styled("tr")({});

const ExpandedBodyRow = styled(BodyRow)({});

const BodyCell = styled("td")(({ theme }) => ({
  ...cellPaddingStyles(theme),
  background: "inherit",
  verticalAlign: "top",
}));

const StickyBodyCell = styled(BodyCell)({
  [`&.${tableClasses.pinnedLeft}`]: {
    position: "sticky",
    left: 0,
    zIndex: 2,
  },
  [`&.${tableClasses.pinnedRight}`]: {
    position: "sticky",
    right: 0,
    zIndex: 2,
  },
});

const StyledHeaderCell = styled(TableHeaderCell)({
  [`&.${tableClasses.pinnedLeft}`]: {
    position: "sticky",
    left: 0,
    zIndex: 2,
  },
  [`&.${tableClasses.pinnedRight}`]: {
    position: "sticky",
    right: 0,
    zIndex: 2,
  },
});

const SortHeaderLinkButton = styled(LinkButton)(({ theme }) => ({
  gap: theme.spacing(0.5),
  [`&:hover .${tableClasses.sortIcon}, &:focus-visible .${tableClasses.sortIcon}`]: {
    opacity: 0.8,
  },
}));

const SortIcon = styled(ArrowUpwardIcon)(({ theme }) => ({
  transition: theme.transitions.create(["color", "transform"]),
  opacity: 0,
  color: theme.palette.text.primary,
  [`.${tableClasses.headerCell}:hover &`]: {
    opacity: 0.8,
  },
  [`&.${tableClasses.sortIconAsc}, &.${tableClasses.sortIconDesc}`]: {
    opacity: "1 !important",
    color: `${theme.palette.text.primary} !important`,
  },
  [`&.${tableClasses.sortIconDesc}`]: {
    transform: "rotate(180deg)",
  },
}));

interface VirtualizationOptions<TData> {
  estimateRowSize: (index: number, row: Row<TData> | undefined) => number;
  overscan?: number;
  scrollPaddingStart?: number;
  scrollPaddingEnd?: number;
}

interface Props<TData> extends Omit<TableOptions<TData>, "getCoreRowModel"> {
  className?: string;

  data: TData[];

  rootRef?: MutableRefObject<HTMLDivElement | null>;
  columns: TableOptions<TData>["columns"];
  pinnedVariant?: PinnedVariant;

  tableTitle?: ReactNode;
  renderDetails?: (props: { row: Row<TData> }) => ReactElement;
  variant?: "lines" | "stripes" | "clear" | "product" | "hsaDetails" | "productComparison" | "";
  fullwidth?: boolean;

  stickyHeader?: boolean;
  scrollbarOnTop?: boolean;
  virtualized?: VirtualizationOptions<TData>;
}

export function TanStackTable<TData>({
  data,
  columns,
  className,
  rootRef,
  renderDetails,
  tableTitle,
  variant = "lines",
  fullwidth,
  stickyHeader,
  scrollbarOnTop,
  pinnedVariant = "solid",
  virtualized,
  ...rest
}: Props<TData>) {
  const isSortingEnabled = columns.some((def) => def.enableSorting);
  const table = useReactTable<TData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isSortingEnabled ? getSortedRowModel() : undefined,
    ...rest,
  });

  const hasHeader = !tableTitle && table.getHeaderGroups().length > 0;
  const allColumnsHaveSize = columns.every((column) => column.size);

  let stickyHeaderVariant: undefined | "simple" | "split" = undefined;
  if (stickyHeader) {
    const hasAnyPinnedColumns = table.getLeafHeaders().some((header) => header.column.getIsPinned());
    if (!hasAnyPinnedColumns) {
      stickyHeaderVariant = "simple";
    } else {
      stickyHeaderVariant = "split";

      // Has pinned columns. Do all have a set width?
      if (!allColumnsHaveSize) {
        console.error("Not implemented: sticky header with pinned columns and no size set.");
      }
    }
  }

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const isVirtualized = virtualized !== undefined && allColumnsHaveSize;

  if (virtualized && !allColumnsHaveSize) {
    console.error("Not implemented: virtualized TanStackTable without explicit column sizes.");
  }

  const enableStuckCornerFix = !!stickyHeader;
  const { ref: stuckRef, isStuck } = useIsStuck<HTMLElement>(enableStuckCornerFix);

  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrolledRight, setIsScrolledRight] = useState(false);
  useEffect(() => {
    if (tableContainerRef.current) {
      const element = tableContainerRef.current;
      const fn = () => {
        const el = tableContainerRef.current;
        if (!el) {
          return;
        }
        setIsScrolled(el.scrollLeft > 0);
        setIsScrolledRight(Math.ceil(el.scrollLeft) + el.clientWidth < el.scrollWidth);
      };
      fn();
      element?.addEventListener("scroll", fn, { passive: true });
      return () => element?.removeEventListener("scroll", fn);
    }
  });

  // Scroll slider state - only track dimensions for showing/hiding slider
  const [scrollDimensions, setScrollDimensions] = useImmer({
    scrollWidth: 0,
    clientWidth: 0,
  });

  // Calculate pinned left width once when columns change
  const pinnedLeftWidth = useMemo(() => {
    const pinnedLeftHeaders = table.getLeftLeafHeaders();
    let totalWidth = 0;
    pinnedLeftHeaders.forEach((header) => {
      const size = getSize(header.column);
      if (size && size.unit === "px") {
        totalWidth += size.value;
      }
    });
    return totalWidth;
  }, [columns]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate if there are scrollable columns (columns beyond pinned left)
  const hasScrollableColumns = table.getCenterLeafHeaders().length > 0 || table.getRightLeafHeaders().length > 0;
  const hasOverflow = scrollDimensions.scrollWidth > scrollDimensions.clientWidth;
  const showScrollSlider = scrollbarOnTop && hasScrollableColumns && hasOverflow;

  // Track dimensions with ResizeObserver - only for determining if slider should show
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) {
      return;
    }

    let rafId: number;

    const updateDimensions = () => {
      setScrollDimensions((draft) => {
        if (tableContainerRef.current) {
          draft.scrollWidth = tableContainerRef.current.scrollWidth;
          draft.clientWidth = tableContainerRef.current.clientWidth;
        }
      });
    };

    const handleUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateDimensions);
    };

    const observer = new ResizeObserver(handleUpdate);
    observer.observe(tableContainer);

    window.addEventListener("resize", handleUpdate, { passive: true });

    updateDimensions();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleUpdate);
      cancelAnimationFrame(rafId);
    };
  }, [setScrollDimensions]);

  function renderTableContainer(children: ReactNode) {
    return (
      <TableContainer
        className={classNames(
          variant !== "productComparison" && stickyHeaderVariant !== "simple" && tableClasses.tableContainer,
        )}
        ref={tableContainerRef}
      >
        {children}
      </TableContainer>
    );
  }
  function renderStickyTableContainer(children: ReactNode) {
    return (
      <StickyTableContainer
        className={classNames(
          variant !== "productComparison" && stickyHeaderVariant !== "simple" && tableClasses.tableContainer,
          tableClasses.sticky,
          stickyHeaderVariant === "split" && tableClasses.hideScrollbar,
          enableStuckCornerFix && tableClasses.stickyCornerSentinel,
        )}
        ref={
          enableStuckCornerFix
            ? (node: HTMLDivElement | null) => {
                stuckRef(node);
                stickyHeaderRef.current = node;
              }
            : stickyHeaderRef
        }
      >
        {children}
      </StickyTableContainer>
    );
  }

  function renderTable(...elements: ReactNode[]) {
    const tableRef =
      enableStuckCornerFix && stickyHeaderVariant === "simple"
        ? (node: HTMLTableElement | null) => stuckRef(node)
        : undefined;

    return (
      <StyledTable
        className={classNames(
          tableClasses.table,
          VARIANTS[variant],
          fullwidth && tableClasses.fullWidth,
          stickyHeaderVariant === "simple" && tableClasses.sticky,
          enableStuckCornerFix && stickyHeaderVariant === "simple" && tableClasses.stickyCornerSentinel,
          allColumnsHaveSize && tableClasses.fixedLayout,
        )}
        ref={tableRef}
      >
        {elements}
      </StyledTable>
    );
  }

  function renderTableHeader() {
    if (!hasHeader) {
      return null;
    }

    return (
      <TableHeader key={"head"} className={classNames(tableClasses.header, stickyHeaderVariant === "simple" && tableClasses.sticky)}>
        <tr>{renderHeaderCells(table, isScrolled, isScrolledRight)}</tr>
      </TableHeader>
    );
  }

  function renderTableBody() {
    const rows = table.getRowModel().rows;

    if (isVirtualized && virtualized) {
      return (
        <VirtualizedTableBody
          table={table}
          virtualized={virtualized}
          isScrolled={isScrolled}
          isScrolledRight={isScrolledRight}
          renderDetails={renderDetails}
        />
      );
    }

    return (
      <tbody key={"body"}>
        {rows.map((row, idx) => (
          <Fragment key={row.id}>
            <BodyRow className={classNames(tableClasses.bodyRow, idx % 2 ? tableClasses.evenRow : tableClasses.oddRow)}>
              {renderBodyCells(row, isScrolled, isScrolledRight)}
            </BodyRow>
            {row.getIsExpanded() && (
              <ExpandedBodyRow className={classNames(tableClasses.bodyRow, idx % 2 ? tableClasses.evenRow : tableClasses.oddRow)}>
                <td colSpan={row.getVisibleCells().length}>{renderDetails?.({ row })}</td>
              </ExpandedBodyRow>
            )}
          </Fragment>
        ))}
      </tbody>
    );
  }

  return (
    <TanStackTableRoot
      className={classNames(
        className,
        variant === "productComparison" && stickyHeaderVariant !== "simple" && tableClasses.tableContainer,
        `pinned-variant-${pinnedVariant}`,
        isScrolled && tableClasses.isScrolledLeft,
        isScrolledRight && tableClasses.isScrolledRight,
        enableStuckCornerFix && isStuck && tableClasses.isStuck,
      )}
      ref={rootRef}
      data-stuck={enableStuckCornerFix && isStuck ? "true" : undefined}
    >
      {tableTitle && <TableTitle className={tableClasses.title}>{tableTitle}</TableTitle>}
      {showScrollSlider && (
        <TableScrollController
          classes={{ root: "scroll-slider__root" }}
          tableContainerRef={tableContainerRef}
          stickyHeaderRef={stickyHeaderRef}
          pinnedLeftWidth={pinnedLeftWidth}
          stickyHeader={stickyHeader}
        />
      )}
      {stickyHeaderVariant !== "split" && renderTableContainer(renderTable(renderTableHeader(), renderTableBody()))}
      {stickyHeaderVariant === "split" && (
        <>
          {renderStickyTableContainer(renderTable(renderTableHeader()))}
          {/*{renderStickyTableContainer(renderTable(renderTableHeader(), renderTableBody({ visible: false })))}*/}
          {renderTableContainer(renderTable(renderTableBody()))}
        </>
      )}
    </TanStackTableRoot>
  );
}

function renderSpacerRow<TData>(row: Row<TData> | undefined, height: number, key: string) {
  if (!row) {
    return null;
  }

  return (
    <tr key={key} aria-hidden="true">
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          style={{
            width: getWidthFromColumn(cell.column),
            height,
            padding: 0,
            border: "none",
            background: "transparent",
          }}
        />
      ))}
    </tr>
  );
}

interface VirtualizedTableBodyProps<TData> {
  table: Table<TData>;
  virtualized: VirtualizationOptions<TData>;
  isScrolled: boolean;
  isScrolledRight: boolean;
  renderDetails?: (props: { row: Row<TData> }) => ReactElement;
}

function VirtualizedTableBody<TData>({
  table,
  virtualized,
  isScrolled,
  isScrolledRight,
  renderDetails,
}: VirtualizedTableBodyProps<TData>) {
  const rows = table.getRowModel().rows;
  const virtualBodyRef = useRef<HTMLTableSectionElement>(null);
  const initialOffset = useMemo(() => makeInitialWindowOffset(virtualBodyRef), []);
  const observeElementOffset = useMemo(() => makeObserveWindowOffset(virtualBodyRef), []);
  const scrollToFn = useMemo(() => makeWindowScrollOffset(virtualBodyRef), []);

  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    getItemKey: (index) => rows[index]?.id ?? String(index),
    estimateSize: (index) => virtualized.estimateRowSize(index, rows[index]),
    overscan: virtualized.overscan ?? 8,
    initialOffset,
    scrollPaddingEnd: virtualized.scrollPaddingEnd ?? 200,
    scrollPaddingStart: virtualized.scrollPaddingStart ?? 200,
    // @ts-expect-error TanStack virtualizer expects a narrower callback signature here.
    observeElementOffset,
    scrollToFn,
  });

  const expandedState = table.getState().expanded;
  useEffect(() => {
    rowVirtualizer.measure();
  }, [expandedState, rowVirtualizer]);

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const firstVisibleStart = virtualItems[0]?.start ?? 0;
  const lastVisibleEnd = virtualItems[virtualItems.length - 1]?.end ?? 0;
  const bottomSpacerHeight = totalSize - lastVisibleEnd;

  return (
    <Fragment key={"body"}>
      <tbody ref={virtualBodyRef}>
        {firstVisibleStart > 0 && renderSpacerRow(rows[0], firstVisibleStart, "top-spacer")}
      </tbody>
      {virtualItems.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (!row) {
          return null;
        }

        const idx = virtualRow.index;
        return (
          <tbody key={row.id} data-index={virtualRow.index} ref={(node) => rowVirtualizer.measureElement(node)}>
            <BodyRow className={classNames(tableClasses.bodyRow, idx % 2 ? tableClasses.evenRow : tableClasses.oddRow)}>
              {renderBodyCells(row, isScrolled, isScrolledRight)}
            </BodyRow>
            {row.getIsExpanded() && (
              <ExpandedBodyRow className={classNames(tableClasses.bodyRow, idx % 2 ? tableClasses.evenRow : tableClasses.oddRow)}>
                <td colSpan={row.getVisibleCells().length}>{renderDetails?.({ row })}</td>
              </ExpandedBodyRow>
            )}
          </tbody>
        );
      })}
      <tbody>{bottomSpacerHeight > 0 && renderSpacerRow(rows[0], bottomSpacerHeight, "bottom-spacer")}</tbody>
    </Fragment>
  );
}

const getWidthFromColumn = <T extends RowData>(column: Column<T>) => {
  const size = getSize(column);
  if (!size) {
    return undefined;
  }
  return getCssSize(size);
};

const CLASS_FACTORIES: ((
  isLeftPinned: boolean,
  isLastLeftLeaf: boolean,
  isFirstRightLeaf: boolean,
  isRightPinned: boolean,
) => (string | boolean)[])[] = [
  (isLeftPinned: boolean, isLastLeftLeaf: boolean) => [
    isLeftPinned && tableClasses.pinnedLeft,
    isLastLeftLeaf && tableClasses.pinnedLeftLast,
    isLastLeftLeaf && "pinned-left-last",
  ],
  () => [],
  (_1, _2, isFirstRightLeaf: boolean, isRightPinned: boolean) => [
    isRightPinned && tableClasses.pinnedRight,
    isFirstRightLeaf && tableClasses.pinnedRightFirst,
    isFirstRightLeaf && "pinned-right-first",
  ],
];

const CELL_TYPE_CLASSES = {
  data: tableClasses.cellData,
  action: tableClasses.cellAction,
};

function renderHeaderCells<TData>(table: Table<TData>, isScrolled: boolean, isScrolledRight: boolean) {
  const cells = [table.getLeftLeafHeaders(), table.getCenterLeafHeaders(), table.getRightLeafHeaders()] as const;
  return cells.map((cells, cGroupIdx) => (
    <Fragment key={cGroupIdx}>
      {cells.map((header, idx, list) => {
        const isLeftPinned = cGroupIdx === 0;
        const isLastLeftLeaf = isLeftPinned && list[list.length - 1] === header;
        const isRightPinned = cGroupIdx === 2;
        const isFirstRightLeaf = isRightPinned && list[0] === header;
        const column = header.column;
        const meta = column.columnDef?.meta ?? {};
        const type = meta.columnType ?? "data";
        const textAlign = meta.align ?? "left";
        const sideClasses = CLASS_FACTORIES[cGroupIdx](isLeftPinned, isLastLeftLeaf, isFirstRightLeaf, isRightPinned);

        const cls = classNames(tableClasses.headerCell, sideClasses, CELL_TYPE_CLASSES[type], meta.className);

        const style: CSSProperties = {
          width: getWidthFromColumn(column),
          textAlign: textAlign,
        };
        if (column.getIsPinned() === "left") {
          const sizes = list
            .slice(0, list.indexOf(header))
            .map((c) => c.column)
            .map(getSize);
          style.left = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        } else if (column.getIsPinned() === "right") {
          const sizes = list
            .slice(list.indexOf(header) + 1)
            .map((c) => c.column)
            .map(getSize);
          style.right = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        }

        const canSort = header.column.columnDef.enableSorting === true;
        const isSorted = header.column.getIsSorted();

        let content;
        if (header.isPlaceholder) {
          content = null;
        } else {
          if (!canSort) {
            content = flexRender(header.column.columnDef.header, header.getContext());
          } else {
            content = (
              <SortHeaderLinkButton
                variant={isSorted ? LinkType.PRIMARY : LinkType.NAVIGATION}
                onClick={() => header.column.toggleSorting(isSorted === "asc")}
                className={tableClasses.sortHeader}
              >
                <SortIcon
                  className={classNames(
                    tableClasses.sortIcon,
                    isSorted === "desc" && tableClasses.sortIconDesc,
                    isSorted === "asc" && tableClasses.sortIconAsc,
                  )}
                  sx={{ fontSize: 10 }}
                />
                <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
              </SortHeaderLinkButton>
            );
          }
          if (meta.tooltip) {
            content = (
              <PopperTooltip tooltipContent={meta.tooltip} className={"d-i-flex"}>
                {content}
              </PopperTooltip>
            );
          }
        }

        return (
          <StyledHeaderCell key={header.id} colSpan={header.colSpan} className={cls} style={style}>
            {content}
          </StyledHeaderCell>
        );
      })}
    </Fragment>
  ));
}
function renderBodyCells<TData>(row: Row<TData>, isScrolled: boolean, isScrolledRight: boolean) {
  const cells = [row.getLeftVisibleCells(), row.getCenterVisibleCells(), row.getRightVisibleCells()] as const;

  return cells.map((cells, cGroupIdx) => (
    <Fragment key={cGroupIdx}>
      {cells.map((cell, idx, list) => {
        const isLeftPinned = cGroupIdx === 0;
        const isLastLeftLeaf = isLeftPinned && list[list.length - 1] === cell;
        const isRightPinned = cGroupIdx === 2;
        const isFirstRightLeaf = isRightPinned && list[0] === cell;
        const column = cell.column;
        const meta = column.columnDef?.meta ?? {};
        const type = meta.columnType ?? "data";
        const textAlign = meta.align ?? "left";
        const sideClasses = CLASS_FACTORIES[cGroupIdx](isLeftPinned, isLastLeftLeaf, isFirstRightLeaf, isRightPinned);

        const getBodyCellClasses = meta.getBodyCellClasses;

        const cls = classNames(
          tableClasses.bodyCell,
          sideClasses,
          CELL_TYPE_CLASSES[type],
          meta.className,
          getBodyCellClasses?.(row),
        );

        const style: CSSProperties = {
          width: getWidthFromColumn(column),
          textAlign: textAlign,
        };
        if (column.getIsPinned() === "left") {
          const sizes = list
            .slice(0, list.indexOf(cell))
            .map((c) => c.column)
            .map(getSize);
          style.left = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        } else if (column.getIsPinned() === "right") {
          const sizes = list
            .slice(list.indexOf(cell) + 1)
            .map((c) => c.column)
            .map(getSize);
          style.right = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        }

        return (
          <StickyBodyCell className={cls} key={cell.id} style={style}>
            {flexRender(column.columnDef.cell, cell.getContext())}
          </StickyBodyCell>
        );
      })}
    </Fragment>
  ));
}
